import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect } from 'react'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import Button from '../ui/buttons/Button'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import IconClose from '../ui/icons/IconClose'
import IconDownload from '../ui/icons/IconDownload'
import IconQrCode from '../ui/icons/IconQrCode'
import TextHeadline from '../ui/texts/TextHeadline'
import IconReloadRed from '../ui/icons/IconReloadRed'

export type SaveCardBeforeReplayVariant = 'default' | 'stele'

type SaveCardBeforeReplayModalProps = {
    open: boolean
    variant: SaveCardBeforeReplayVariant
    onClose: () => void
    onPrimaryAction: () => void
    onReplayAnyway: () => void
    position?: 'absolute' | 'fixed'
}

const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2, ease: 'easeOut' as const } },
    exit: { opacity: 0, transition: { duration: 0.2, ease: 'easeIn' as const } },
}

const cardVariants = {
    hidden: { opacity: 0, scale: 0.85, y: 20 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: 'spring' as const,
            stiffness: 380,
            damping: 22,
            mass: 0.8,
        },
    },
    exit: { opacity: 0, scale: 0.9, transition: { duration: 0.2 } },
}

const contentByVariant = {
    default: {
        headline: 'Sammelkarte Vorher Speichern?',
        headlineMaxCharsPerLine: 17,
        body: 'Beim nächsten Lauf wird diese Karte ersetzt.',
        primaryLabel: 'Karte Speichern',
    },
    stele: {
        headline: 'Karte Vorher Mitnehmen?',
        headlineMaxCharsPerLine: 22,
        body: 'Beim nächsten Lauf wird diese Karte ersetzt. Scanne den QR-Code, um sie auf deinem Handy zu behalten.',
        primaryLabel: 'QR-Code Anzeigen',
    },
} as const

export default function SaveCardBeforeReplayModal({
    open,
    variant,
    onClose,
    onPrimaryAction,
    onReplayAnyway,
    position = 'absolute',
}: SaveCardBeforeReplayModalProps) {
    const isStele = useSteleBreakpoint()
    const content = contentByVariant[variant]

    useEffect(() => {
        if (!open) return
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, onClose])

    const primaryIcon = variant === 'stele'
        ? <IconQrCode width='100%' height='100%' />
        : <IconDownload width='100%' height='100%' />

    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    data-ui-priority
                    className={clsx(
                        position === 'fixed' ? 'fixed z-[70]' : 'absolute z-50',
                        'inset-0 flex justify-center items-center bg-black/55 backdrop-blur-sm pointer-events-auto',
                        'px-4',
                        'stele:px-24',
                    )}
                    variants={overlayVariants}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                    role='dialog'
                    aria-modal='true'
                    aria-labelledby='save-card-before-replay-title'
                >
                    <div className='relative'>
                        <motion.div
                            className={clsx(
                                'relative flex flex-col justify-center items-center drop-shadow-2-black-stroke bg-white-stroke-even-darker border-black-stroke pattern-background',
                                'gap-4 px-6 pt-12 pb-8 border rounded-4xl w-full max-w-100',
                                'stele:gap-18 stele:px-24 stele:pt-32 stele:pb-48 stele:border-4 stele:rounded-[128px] stele:max-w-400',
                            )}
                            variants={cardVariants}
                        >
                            <TextHeadline
                                className={clsx(
                                    'left-1/2 absolute -translate-x-1/2',
                                    '-top-16',
                                    'stele:-top-60',
                                )}
                                colorPreset='red'
                                fontSize={isStele ? 128 : 32}
                                maxCharsPerLine={content.headlineMaxCharsPerLine}
                                leading={0.95}
                                tracking='-2%'
                            >
                                {content.headline}
                            </TextHeadline>

                            <p
                                className={clsx(
                                    'text-white-stroke-lightest font-nunito font-bold text-center tracking-[-2%]',
                                    'text-[16px] leading-5.5',
                                    'stele:text-[64px] stele:leading-22',
                                )}
                                style={{ textShadow: isStele ? '0 4px 0 #06487C' : '0 1px 0 #06487C' }}
                            >
                                {content.body}
                            </p>

                            <div
                                className={clsx(
                                    'flex flex-col w-full',
                                    'gap-4 pb-2',
                                    'stele:gap-8',
                                )}
                            >
                                <Button
                                    type='button'
                                    color='blue'
                                    size='large'
                                    icon={primaryIcon}
                                    onClick={onPrimaryAction}
                                >
                                    {content.primaryLabel}
                                </Button>
                                <Button
                                    type='button'
                                    color='red'
                                    icon={<IconReloadRed width='100%' height='100%' />}
                                    onClick={onReplayAnyway}
                                >
                                    Trotzdem Nochmal Spielen
                                </Button>
                            </div>
                        </motion.div>

                        <div
                            className={clsx(
                                'left-1/2 absolute -translate-x-1/2',
                                '-bottom-6',
                                'stele:-bottom-24',
                            )}
                        >
                            <ButtonIcon
                                color='red'
                                variant='standalone'
                                popupCloseSound
                                icon={<IconClose width='100%' height='100%' />}
                                onClick={onClose}
                                aria-label='Schließen'
                            />
                        </div>
                    </div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    )
}
